/**
 * Shared legal page content — single source for React UI and SSR HTML.
 * Canonical Spanish URLs for LSSI / AdSense trust pages.
 */

export const LEGAL_CONTACT_EMAIL = 'statemia@statemia.com';
export const LEGAL_COMPANY_NAME = 'WEWORK FACTORY SL';
export const LEGAL_NIF = 'B86131380';
export const LEGAL_ADDRESS = 'Calle Emilio Muñoz 31º, 28037 Madrid (Madrid), España';
/** Stable last-updated date shown on legal pages (ISO date). */
export const LEGAL_LAST_UPDATED = '2026-08-08';

export const LEGAL_SECTION_IDS = [
  'about',
  'contact',
  'legal',
  'privacy',
  'cookies',
  'terms',
  'social-privacy',
] as const;

export type LegalSectionId = (typeof LEGAL_SECTION_IDS)[number];

/** Public canonical path without leading domain (e.g. /aviso-legal). */
export type LegalSlug =
  | 'sobre-statemia'
  | 'contacto'
  | 'aviso-legal'
  | 'politica-de-privacidad'
  | 'politica-de-cookies'
  | 'terminos-de-servicio';

export interface LegalBlock {
  heading?: string;
  paragraphs?: string[];
  list?: string[];
  definitionLines?: Array<{ label: string; value: string }>;
  subBlocks?: Array<{ heading: string; paragraph: string }>;
}

export interface LegalPage {
  id: LegalSectionId;
  /** Canonical Spanish URL path segment(s), without leading slash issues — full path. */
  slug: LegalSlug | null;
  navTitle: string;
  title: string;
  intro?: string;
  metaDescription: string;
  showLastUpdated?: boolean;
  blocks: LegalBlock[];
}

const COMPANY_LINES: Array<{ label: string; value: string }> = [
  { label: 'Denominación social', value: LEGAL_COMPANY_NAME },
  { label: 'NIF', value: LEGAL_NIF },
  { label: 'Domicilio social', value: LEGAL_ADDRESS },
  { label: 'Correo electrónico', value: LEGAL_CONTACT_EMAIL },
  { label: 'Sitio web', value: 'https://www.statemia.com' },
];

export const LEGAL_PAGES: Record<LegalSectionId, LegalPage> = {
  about: {
    id: 'about',
    slug: 'sobre-statemia',
    navTitle: 'Sobre Statemia',
    title: 'Sobre Statemia',
    intro:
      'Statemia es un medio editorial de periodismo de datos en español: artículos basados en cifras, contexto y fuentes verificables.',
    metaDescription:
      'Sobre Statemia: medio de periodismo de datos en español. Misión, valores y equipo editorial de WEWORK FACTORY SL.',
    showLastUpdated: true,
    blocks: [
      {
        heading: 'Nuestra misión',
        paragraphs: [
          'En Statemia creemos que el análisis de datos debe ser accesible para todos. Publicamos historias basadas en números para ayudar a entender la realidad con rigor, claridad y transparencia.',
        ],
      },
      {
        heading: 'Nuestros valores',
        list: [
          'Rigor editorial y fuentes citadas',
          'Transparencia en metodología y estimaciones',
          'Accesibilidad del periodismo de datos',
          'Privacidad y seguridad por diseño',
          'Independencia editorial',
        ],
      },
      {
        heading: 'Quiénes somos',
        paragraphs: [
          'Dirección editorial: [Alejandro Rayón](/autor/alejandro-rayon). Statemia es un medio de periodismo de datos en español. La titularidad del proyecto corresponde a WEWORK FACTORY SL.',
        ],
      },
      {
        heading: 'Contacto',
        definitionLines: [
          { label: 'Email editorial', value: LEGAL_CONTACT_EMAIL },
          { label: 'Titular', value: LEGAL_COMPANY_NAME },
          { label: 'NIF', value: LEGAL_NIF },
          { label: 'Dirección', value: LEGAL_ADDRESS },
        ],
      },
    ],
  },

  contact: {
    id: 'contact',
    slug: 'contacto',
    navTitle: 'Contacto',
    title: 'Contacto',
    intro: 'Puedes contactar con el equipo de Statemia de forma directa y gratuita.',
    metaDescription:
      'Contacto de Statemia: email editorial, domicilio en Madrid y horarios de atención. WEWORK FACTORY SL.',
    showLastUpdated: true,
    blocks: [
      {
        heading: 'Correo electrónico',
        paragraphs: [
          'Escríbenos y te responderemos lo antes posible (habitualmente en menos de 24–48 horas laborables).',
          LEGAL_CONTACT_EMAIL,
        ],
      },
      {
        heading: 'Domicilio',
        paragraphs: [
          'Nuestra sede está en Madrid.',
          `${LEGAL_COMPANY_NAME}. ${LEGAL_ADDRESS}`,
        ],
      },
      {
        heading: 'Horarios de atención',
        subBlocks: [
          {
            heading: 'Soporte y redacción',
            paragraph: 'Lunes a viernes: 9:00 – 18:00 (CET).',
          },
          {
            heading: 'Consultas comerciales',
            paragraph: 'Lunes a viernes: 9:00 – 17:00 (CET). Respuesta en 24–48 horas.',
          },
        ],
      },
      {
        heading: 'Identificación del prestador',
        definitionLines: COMPANY_LINES,
      },
    ],
  },

  legal: {
    id: 'legal',
    slug: 'aviso-legal',
    navTitle: 'Aviso legal',
    title: 'Aviso legal',
    intro:
      'Información legal del prestador de servicios de la sociedad de la información (LSSI) relativa a Statemia y a WEWORK FACTORY SL.',
    metaDescription:
      'Aviso legal de Statemia (WEWORK FACTORY SL, NIF B86131380): identificación del prestador, titularidad, propiedad intelectual y responsabilidad.',
    showLastUpdated: true,
    blocks: [
      {
        heading: '1. Datos identificativos del prestador',
        paragraphs: [
          'En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI), se informa de los siguientes datos del prestador:',
        ],
        definitionLines: COMPANY_LINES,
      },
      {
        heading: '2. Titularidad de Statemia',
        paragraphs: [
          'Statemia (https://www.statemia.com) es un medio digital de periodismo de datos cuya titularidad corresponde a WEWORK FACTORY SL. La marca, el dominio y los contenidos editoriales publicados en la plataforma son explotados por dicha sociedad, salvo indicación expresa en contrario.',
        ],
      },
      {
        heading: '3. Condiciones de uso',
        paragraphs: [
          'El acceso y uso de Statemia implica la aceptación de las condiciones descritas en este aviso legal y en los Términos de servicio disponibles en /terminos-de-servicio. El usuario se compromete a un uso lícito, diligente y conforme a la legislación aplicable.',
        ],
      },
      {
        heading: '4. Propiedad intelectual e industrial',
        paragraphs: [
          'Los textos, gráficos, experiencias interactivas, logotipos, diseño, código y demás elementos de Statemia son propiedad de WEWORK FACTORY SL o de sus licenciantes, y están protegidos por la normativa española e internacional de propiedad intelectual e industrial. Queda prohibida su reproducción, distribución o comunicación pública sin autorización previa, salvo el derecho de cita con mención de la fuente.',
        ],
      },
      {
        heading: '5. Responsabilidad sobre cálculos, estimaciones y contenidos',
        paragraphs: [
          'Los artículos de Statemia pueden incluir cifras, ratios, proyecciones y estimaciones elaboradas a partir de fuentes públicas o privadas citadas. Dichos contenidos tienen finalidad informativa y periodística. No constituyen asesoramiento profesional (financiero, jurídico, médico u otro).',
          'Aunque procuramos rigor metodológico, WEWORK FACTORY SL no garantiza la exactitud absoluta, exhaustividad ni actualización permanente de todos los datos. El usuario debe contrastar la información cuando tome decisiones relevantes.',
        ],
      },
      {
        heading: '6. Política de enlaces y fuentes',
        paragraphs: [
          'Statemia puede incluir enlaces a sitios de terceros (fuentes estadísticas, organismos, medios, Amazon u otros). Dichos sitios disponen de sus propias políticas; no somos responsables de sus contenidos ni de sus prácticas de privacidad.',
          'Cuando un artículo cite fuentes, se indica la procedencia para facilitar la verificación. Los enlaces de afiliados, si existen, se señalan de forma transparente.',
        ],
      },
      {
        heading: '7. Limitación de responsabilidad',
        paragraphs: [
          'En la medida permitida por la ley, WEWORK FACTORY SL no será responsable de daños indirectos, lucro cesante o perjuicios derivados del uso o la imposibilidad de uso del sitio, ni de interrupciones técnicas ajenas a su control razonable.',
        ],
      },
      {
        heading: '8. Contacto editorial y legal',
        paragraphs: [
          `Para consultas editoriales, derechos de imagen, rectificaciones o cuestiones legales relacionadas con Statemia: ${LEGAL_CONTACT_EMAIL}.`,
        ],
      },
      {
        heading: '9. Ley aplicable y jurisdicción',
        paragraphs: [
          'Este aviso legal se rige por la legislación española. Salvo normas imperativas en contrario, las partes se someten a los juzgados y tribunales de Madrid (España).',
        ],
      },
    ],
  },

  privacy: {
    id: 'privacy',
    slug: 'politica-de-privacidad',
    navTitle: 'Privacidad',
    title: 'Política de privacidad',
    intro:
      'Esta política describe cómo WEWORK FACTORY SL trata los datos personales en relación con Statemia (https://www.statemia.com), de conformidad con el RGPD y la LOPDGDD.',
    metaDescription:
      'Política de privacidad de Statemia: datos personales, Google Analytics, Google AdSense, cookies publicitarias y derechos RGPD. Contacto: statemia@statemia.com.',
    showLastUpdated: true,
    blocks: [
      {
        heading: '1. Responsable del tratamiento',
        definitionLines: [
          { label: 'Responsable', value: LEGAL_COMPANY_NAME },
          { label: 'NIF', value: LEGAL_NIF },
          { label: 'Domicilio', value: LEGAL_ADDRESS },
          { label: 'Email', value: LEGAL_CONTACT_EMAIL },
        ],
      },
      {
        heading: '2. Datos que recopilamos',
        paragraphs: [
          'Podemos tratar datos que nos facilitas al crear una cuenta, suscribirte, contactarnos o navegar por el sitio:',
        ],
        list: [
          'Datos de cuenta (nombre, correo electrónico)',
          'Preferencias de perfil y de cookies',
          'Datos de pago gestionados por Stripe (no almacenamos el número completo de tarjeta)',
          'Comunicaciones de soporte',
          'Datos de uso y analítica (de forma agregada o seudonimizada cuando procede)',
          'Contenido que creas o guardas en la plataforma (p. ej. favoritos)',
        ],
      },
      {
        heading: '3. Finalidades y bases jurídicas',
        list: [
          'Prestar y mejorar el servicio (ejecución de contrato / interés legítimo)',
          'Gestionar suscripciones y pagos (ejecución de contrato)',
          'Atender solicitudes y soporte (interés legítimo / medidas precontractuales)',
          'Cumplir obligaciones legales',
          'Analítica de audiencia con Google Analytics, solo con tu consentimiento',
          'Mostrar publicidad (incluida personalizada) mediante Google AdSense y redes asociadas, solo con tu consentimiento de cookies de publicidad',
        ],
      },
      {
        heading: '4. Google, AdSense y publicidad',
        paragraphs: [
          'Statemia puede utilizar Google AdSense y servicios de Google para mostrar anuncios y medir su rendimiento. Google y otros proveedores publicitarios pueden usar cookies u identificadores similares para:',
        ],
        list: [
          'Mostrar anuncios contextuales o personalizados según tus preferencias de consentimiento',
          'Limitar la frecuencia de anuncios y medir conversiones',
          'Detectar fraude e infracciones de políticas',
        ],
      },
      {
        paragraphs: [
          'La publicidad personalizada solo se activa si aceptas las cookies de publicidad en nuestro banner de consentimiento (Consent Mode v2). Puedes rechazar o retirar el consentimiento en cualquier momento con la misma facilidad que para aceptarlo.',
          'Opciones de exclusión y control de anuncios personalizados de Google: https://adssettings.google.com/ y https://www.youronlinechoices.eu/. Más información en la política de privacidad de Google: https://policies.google.com/privacy',
          'Publisher ID de AdSense utilizado en Statemia: ca-pub-3349161084383893.',
        ],
      },
      {
        heading: '5. Redes sociales',
        paragraphs: [
          'Si interactúas con Statemia en plataformas de terceros (p. ej. X/Twitter, LinkedIn, Facebook), esas plataformas tratan datos según sus propias políticas. Podemos ver información pública de perfil, comentarios o métricas de engagement necesarias para responderte o mejorar la comunicación. No publicamos automáticamente datos privados de tu cuenta de Statemia en redes sociales.',
        ],
      },
      {
        heading: '6. Destinatarios y encargados',
        paragraphs: [
          'Podemos compartir datos con proveedores que nos prestan servicios (hosting, email, pagos Stripe, analítica y publicidad Google), bajo contratos de encargo o como responsables independientes cuando así lo indique la normativa. No vendemos tus datos personales.',
        ],
      },
      {
        heading: '7. Conservación y seguridad',
        paragraphs: [
          'Conservamos los datos el tiempo necesario para las finalidades indicadas y las obligaciones legales. Aplicamos medidas técnicas y organizativas razonables (incluido HTTPS) para proteger la información.',
        ],
      },
      {
        heading: '8. Tus derechos',
        paragraphs: ['Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad, así como retirar el consentimiento cuando la base sea el consentimiento.'],
        list: [
          'Solicitudes: escribe a ' + LEGAL_CONTACT_EMAIL,
          'Reclamación ante la Agencia Española de Protección de Datos (AEPD): https://www.aepd.es/',
        ],
      },
      {
        heading: '9. Más información sobre cookies',
        paragraphs: [
          'El detalle de tipos de cookies, finalidades y gestión del consentimiento está en /politica-de-cookies.',
        ],
      },
    ],
  },

  cookies: {
    id: 'cookies',
    slug: 'politica-de-cookies',
    navTitle: 'Cookies',
    title: 'Política de cookies',
    intro:
      'Esta política explica qué cookies y tecnologías similares usa Statemia, con qué finalidad y cómo puedes aceptarlas o rechazarlas con la misma facilidad.',
    metaDescription:
      'Política de cookies de Statemia: cookies esenciales, analítica (Google Analytics), publicidad (Google AdSense) y cómo gestionar el consentimiento.',
    showLastUpdated: true,
    blocks: [
      {
        heading: '1. ¿Qué son las cookies?',
        paragraphs: [
          'Las cookies son pequeños archivos que se almacenan en tu dispositivo al visitar un sitio web. También pueden usarse tecnologías similares (almacenamiento local, píxeles). Nos ayudan a hacer funcionar el sitio, recordar preferencias, medir audiencia y, si lo consientes, mostrar publicidad.',
        ],
      },
      {
        heading: '2. Tipos de cookies que utilizamos',
        subBlocks: [
          {
            heading: 'Esenciales / técnicas',
            paragraph:
              'Necesarias para el funcionamiento del sitio (sesión, seguridad, preferencias de consentimiento). No requieren consentimiento.',
          },
          {
            heading: 'Analítica',
            paragraph:
              'Google Analytics (GA4) para entender el uso del sitio de forma agregada. Solo se activan con tu consentimiento de cookies de análisis.',
          },
          {
            heading: 'Preferencias y funcionalidad',
            paragraph:
              'Recuerdan elecciones de interfaz o configuración no esenciales cuando aplica.',
          },
          {
            heading: 'Publicidad (Google AdSense y partners)',
            paragraph:
              'Permiten mostrar anuncios, medir rendimiento y, si lo aceptas, personalizar publicidad. Se activan solo con consentimiento de cookies de publicidad (Consent Mode v2). Publisher ID: ca-pub-3349161084383893.',
          },
        ],
      },
      {
        heading: '3. Cómo gestionar el consentimiento',
        paragraphs: [
          'Al entrar en Statemia verás un banner donde puedes aceptar todas las cookies, rechazarlas (salvo las esenciales) o configurar categorías. Aceptar y rechazar tienen el mismo nivel de visibilidad y facilidad.',
          'Puedes cambiar tu elección más adelante borrando el consentimiento almacenado o mediante la configuración del navegador. Desactivar cookies puede afectar a algunas funciones.',
        ],
      },
      {
        heading: '4. Proveedores externos',
        list: [
          'Google Analytics — analítica; requiere consentimiento de análisis.',
          'Google AdSense — publicidad; requiere consentimiento de publicidad. Política de Google: https://policies.google.com/privacy',
          'Controles de anuncios personalizados: https://adssettings.google.com/ y https://www.youronlinechoices.eu/',
        ],
      },
      {
        heading: '5. Más información',
        paragraphs: [
          `La política de privacidad completa está en /politica-de-privacidad. Contacto: ${LEGAL_CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  terms: {
    id: 'terms',
    slug: 'terminos-de-servicio',
    navTitle: 'Términos',
    title: 'Términos de servicio',
    intro:
      'Al acceder y utilizar Statemia (https://www.statemia.com), aceptas estos términos. Si no estás de acuerdo, no uses el servicio.',
    metaDescription:
      'Términos de servicio de Statemia (WEWORK FACTORY SL): uso aceptable, cuentas, propiedad intelectual, afiliados, publicidad y ley aplicable.',
    showLastUpdated: true,
    blocks: [
      {
        heading: '1. Descripción del servicio',
        paragraphs: [
          'Statemia es una plataforma editorial de periodismo de datos que publica artículos estadísticos, experiencias interactivas y contenido relacionado. Puede incluir cuentas de usuario, favoritos, suscripciones premium y publicidad.',
        ],
      },
      {
        heading: '2. Uso aceptable',
        list: [
          'No uses el sitio para actividades ilegales o abusivas',
          'No intentes vulnerar, sobrecargar o interferir con la infraestructura',
          'No copies, redistribuyas o revendás el contenido sin autorización',
          'No automatices scraping masivo sin permiso previo',
        ],
      },
      {
        heading: '3. Cuentas de usuario',
        paragraphs: [
          'Eres responsable de la confidencialidad de tu cuenta y de la actividad realizada con ella. Podemos suspender cuentas que incumplan estos términos o la ley.',
        ],
      },
      {
        heading: '4. Contenido y propiedad intelectual',
        paragraphs: [
          'El contenido publicado en Statemia es propiedad de WEWORK FACTORY SL o de sus licenciantes, y está protegido por la legislación aplicable.',
        ],
      },
      {
        heading: '5. Enlaces de afiliados (Amazon)',
        paragraphs: [
          'Algunos artículos pueden incluir enlaces de afiliados. Si compras a través de ellos, podemos recibir una comisión sin coste adicional para ti. Esto no altera nuestros criterios editoriales.',
        ],
      },
      {
        heading: '6. Publicidad',
        paragraphs: [
          'El sitio puede mostrar anuncios de Google AdSense u otras redes, sujetos a tu consentimiento de cookies publicitarias. Los anunciantes son responsables de su propio contenido publicitario.',
        ],
      },
      {
        heading: '7. Suscripciones y pagos',
        paragraphs: [
          'Las suscripciones premium se gestionan a través de Stripe. Precios, periodos y cancelaciones se muestran en el proceso de compra.',
        ],
      },
      {
        heading: '8. Limitación de responsabilidad',
        paragraphs: [
          'El contenido se ofrece con fines informativos. No garantizamos exactitud absoluta ni disponibilidad ininterrumpida. En la medida permitida por la ley, WEWORK FACTORY SL no será responsable de daños indirectos derivados del uso del servicio.',
        ],
      },
      {
        heading: '9. Ley aplicable',
        paragraphs: [
          'Estos términos se rigen por la legislación española. Cualquier disputa se someterá a los tribunales de Madrid (España), sin perjuicio de los derechos imperativos del consumidor.',
        ],
      },
      {
        heading: '10. Contacto',
        paragraphs: [`Para consultas sobre estos términos: ${LEGAL_CONTACT_EMAIL}.`],
      },
    ],
  },

  /** Legacy id kept for redirects only — content lives under privacy. */
  'social-privacy': {
    id: 'social-privacy',
    slug: null,
    navTitle: 'Privacidad redes',
    title: 'Política de privacidad en redes sociales',
    metaDescription: 'Redirige a la política de privacidad de Statemia.',
    showLastUpdated: true,
    blocks: [],
  },
};

/** Pages shown in nav / footer (excludes legacy social-privacy). */
export const LEGAL_PUBLIC_SECTION_IDS: LegalSectionId[] = [
  'about',
  'contact',
  'legal',
  'privacy',
  'cookies',
  'terms',
];

const SECTION_BY_SLUG: Record<string, LegalSectionId> = {
  'sobre-statemia': 'about',
  contacto: 'contact',
  'aviso-legal': 'legal',
  'politica-de-privacidad': 'privacy',
  'politica-de-cookies': 'cookies',
  'terminos-de-servicio': 'terms',
};

/** Legacy /legal/:section → canonical path */
export const LEGACY_SECTION_TO_PATH: Record<string, string> = {
  about: '/sobre-statemia',
  contact: '/contacto',
  legal: '/aviso-legal',
  privacy: '/politica-de-privacidad',
  cookies: '/politica-de-cookies',
  terms: '/terminos-de-servicio',
  'social-privacy': '/politica-de-privacidad',
};

export function isLegalSectionId(value: string | undefined | null): value is LegalSectionId {
  return Boolean(value && (LEGAL_SECTION_IDS as readonly string[]).includes(value));
}

export function isLegalSlug(value: string | undefined | null): value is LegalSlug {
  return Boolean(value && value in SECTION_BY_SLUG);
}

export function getLegalPage(section: string | undefined | null): LegalPage | null {
  if (!isLegalSectionId(section)) return null;
  return LEGAL_PAGES[section];
}

export function getLegalPageBySlug(slug: string | undefined | null): LegalPage | null {
  if (!slug || !isLegalSlug(slug)) return null;
  return LEGAL_PAGES[SECTION_BY_SLUG[slug]];
}

export function legalPath(section: LegalSectionId = 'about'): string {
  const page = LEGAL_PAGES[section];
  if (page.slug) return `/${page.slug}`;
  return LEGACY_SECTION_TO_PATH[section] || '/sobre-statemia';
}

export function legacyLegalRedirectPath(sectionOrEmpty: string | undefined | null): string {
  if (!sectionOrEmpty) return '/sobre-statemia';
  if (LEGACY_SECTION_TO_PATH[sectionOrEmpty]) return LEGACY_SECTION_TO_PATH[sectionOrEmpty];
  if (isLegalSlug(sectionOrEmpty)) return `/${sectionOrEmpty}`;
  return '/sobre-statemia';
}

export function formatLegalLastUpdated(locale = 'es-ES'): string {
  const d = new Date(LEGAL_LAST_UPDATED + 'T12:00:00');
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}

export const LEGAL_NAV_ITEMS = LEGAL_PUBLIC_SECTION_IDS.map((id) => ({
  id,
  title: LEGAL_PAGES[id].navTitle,
  href: legalPath(id),
}));

export const LEGAL_SITEMAP_PATHS = LEGAL_PUBLIC_SECTION_IDS.map((id) => legalPath(id));
